import { authRequired } from "@/lib/auth";
import { Home as HomeIcon } from "lucide-react";
import { type ReactElement, lazy } from "react";
import { Navigate, type RouteObject, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthProvider";

/**
 * Wrap a page in this component makes it require authentication to view
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isLoggedIn, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isLoggedIn && authRequired()) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

/**
 * This will redirect a logged-in user to the page they came from or back home.
 * This is useful for pages that should only be accessible to guests, like login or register.
 */
function GuestRoute({ children }: { children: React.ReactNode }) {
  const { isLoggedIn, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (isLoggedIn) {
    // Redirect to the page they came from or app dashboard
    const from = location.state?.from?.pathname || "/";
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
}

const LoginPage = lazy(() => import("../pages/Login"));
const RegisterPage = lazy(() => import("../pages/Register"));
const AuthCallbackPage = lazy(() => import("../pages/AuthCallback"));
const NotFound = lazy(() => import("../pages/NotFound404"));
const DashboardPage = lazy(() => import('../pages/DashboardPage'));
const ProjectPage = lazy(() => import('../pages/ProjectPage'));
const IssuePage = lazy(() => import('../pages/IssuePage'));
const TriagePage = lazy(() => import('../pages/TriagePage'));
const InboxPage = lazy(() => import('../pages/InboxPage'));
const MyIssuesPage = lazy(() => import('../pages/MyIssuesPage'));
const SearchPage = lazy(() => import('../pages/SearchPage'));
const TeamPage = lazy(() => import('../pages/TeamPage'));


const routes = [
  // IMPORTANT: Pages should use ProtectedRoute if they include any functionality
  // the user might want to limit access to. In most cases the entire app should be
  // protected.
  { path: "/", element: <ProtectedRoute><DashboardPage /></ProtectedRoute> },
  { path: "/project/:projectId", element: <ProtectedRoute><ProjectPage /></ProtectedRoute> },
  { path: "/issue/:issueId", element: <ProtectedRoute><IssuePage /></ProtectedRoute> },
  { path: "/issue/:issueNumber", element: <ProtectedRoute><IssuePage /></ProtectedRoute> },
  { path: "/triage", element: <ProtectedRoute><TriagePage /></ProtectedRoute> },
  { path: "/inbox", element: <ProtectedRoute><InboxPage /></ProtectedRoute> },
  { path: "/my-issues", element: <ProtectedRoute><MyIssuesPage /></ProtectedRoute> },
  { path: "/search", element: <ProtectedRoute><SearchPage /></ProtectedRoute> },
  { path: "/team", element: <ProtectedRoute><TeamPage /></ProtectedRoute> },
  
  {
    path: "/login",
    element: (
      <GuestRoute>
        <LoginPage />
      </GuestRoute>
    ),
  },
  {
    path: "/register",
    element: (
      <GuestRoute>
        <RegisterPage />
      </GuestRoute>
    ),
  },
  { path: "/auth/callback", element: <AuthCallbackPage /> },
  { path: "*", element: <NotFound /> },
] as const;
export const routesExcludedFromSidebar = ["/login", "/register", "/auth/callback", "*"] as const;

// This type ensures that the paths are a valid top level path
type AllRoutes = (typeof routes)[number]["path"];
type RoutesExcludedFromSidebar = (typeof routesExcludedFromSidebar)[number];
export type ValidSidebarPath = Exclude<AllRoutes, RoutesExcludedFromSidebar>;
export const sidebarRoutes = [
  {
    path: "/",
    title: "Home",
    icon: <HomeIcon />,
    breadcrumb: { section: "Platform", page: "home" },
  },
  // Add route here to add it to the sidebar
] satisfies Array<{
  title: string;
  path: ValidSidebarPath;
  icon: ReactElement;
  breadcrumb: { section: string; page: string };
}>;

export default [...routes] satisfies RouteObject[];
